import { ReactElement, useMemo, useCallback } from 'react';
import { Stack } from '@mui/material';
import { SelectRowProps, TableChangeHandler } from 'react-bootstrap-table-next';

import { NotificationRefreshItem } from '~globals/types/notifications';
import { ItemExtended } from '~services/item/types';
import { useSignalRContext } from '~contexts/SignalRContext';
import { useAppSelector } from '~hooks/useAppSelector';
import {
  useDataTableSelectedProps,
  SelectRowsHandlerOnSelectAll,
  SelectRowsHandlerOnSelect,
} from '~hooks/useDataTableSelectedProps';
import { useClientOnMethod } from '~hooks/useSignalR';
import { selectAuth } from '~store/slices/auth/index';
import { isOnlyReadUser } from '~utils/user';
import { METHODS_NAME } from '~constants/signalR';
import {
  GeoError,
  CustomNoRecordsIndicator,
  DataTable,
  DialogDetailsOrder,
} from '~components/index';
import NoRecordsFoundMessage from '~components/DataTable/NoRecordsFoundMessage';

import { useDashboardVariantV2GiverContext } from '../../../../../Context';
// eslint-disable-next-line max-len
import { useDashboardVariantV2GiverInPreparationFiltersContext } from '../../../../../InPreparationFiltersContext';
import { useDashboardVariantV2GiverContentInPreparationTabContext } from '../../Context';

import { ITEM_KEY_ID } from '../../utils';
import { DashboardVariantV2GiverContentDataTableContainer } from '../../../../styles';

import { useInPreparationTabDataTableColumns } from './useColumns';

const DashboardVariantV2GiverContentInPreparationTabDataTable =
  (): ReactElement => {
    const { hubConnection } = useSignalRContext();
    const { user } = useAppSelector(selectAuth);

    const isUserOnlyReader = useMemo(() => isOnlyReadUser(user), [user]);

    const {
      actions: {
        ignoreRefreshBySocket: {
          value: ignoreRefreshBySocket,
          toggleValue: toggleIgnoreRefreshBySocket,
        },
        expandedHasChangeOrdersAlert: {
          toggleValue: toggleExpandedHasChangeOrdersAlert,
        },
        modifiedItemsIdBySocket: { setValue: setModifiedItemsIdBySocket },
      },
      dialogs: {
        importFromFile: { toggleOpen: toggleOpenDialogImportFromFile },
        importFromIntegration: {
          toggleOpen: toggleOpenDialogImportFromIntegration,
        },
        updateGeoError: {
          open: openDialogUpdateGeoError,
          toggleOpen: toogleOpenDialogUpdateGeoError,
        },
      },
    } = useDashboardVariantV2GiverContext();

    const {
      filters: { isFilterApplied },
    } = useDashboardVariantV2GiverInPreparationFiltersContext();

    const {
      selection: { selectedIds, utils: selectedIdsUtils },
      itemsWithGeoErrors,
      table: {
        data,
        loading: loadingRequestTable,
        totalSize,
        queryParams: { pagination },
        handleChangeTable: changeTable,
      },
      actions: { reloadDataAndResetSelectedRows },
      dialogs: {
        detailsOrder: {
          open: openDialogDetailsOrder,
          data: selectedDataForDetailsOrder,
          handleClose: handleCloseDialogDetailsOrder,
        },
      },
    } = useDashboardVariantV2GiverContentInPreparationTabContext();

    const columns = useInPreparationTabDataTableColumns();

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleChangeTable = useCallback<TableChangeHandler<any>>(
      (type, newState) => {
        toggleExpandedHasChangeOrdersAlert(false);
        toggleIgnoreRefreshBySocket(false);
        setModifiedItemsIdBySocket([]);

        changeTable(type, {
          pagination: { ...newState, sortField: newState?.sortField },
        });
      },
      [
        changeTable,
        toggleExpandedHasChangeOrdersAlert,
        toggleIgnoreRefreshBySocket,
        setModifiedItemsIdBySocket,
      ],
    );

    const handleOnSelectAll = useCallback<
      SelectRowsHandlerOnSelectAll<ItemExtended>
    >(
      (isSelected, rows) => {
        if (isSelected) {
          const ids = rows.map((row) => row[ITEM_KEY_ID]);
          selectedIdsUtils.addMultiple(ids);
        } else {
          selectedIdsUtils.reset();
        }
      },
      [selectedIdsUtils],
    );

    const handleOnSelect = useCallback<SelectRowsHandlerOnSelect<ItemExtended>>(
      (row) => {
        const currentId = row[ITEM_KEY_ID];
        selectedIdsUtils.toggle(currentId);
      },
      [selectedIdsUtils],
    );

    const selectRowsProps = useDataTableSelectedProps({
      onSelectAll: handleOnSelectAll,
      onSelect: handleOnSelect,
      selectedIds,
    });

    const dataTableSelectRowProps = useMemo<
      SelectRowProps<ItemExtended> | undefined
    >(
      () => (isUserOnlyReader ? undefined : selectRowsProps),
      [isUserOnlyReader, selectRowsProps],
    );

    const getNoRecordsIndicator = useCallback(() => {
      if (isFilterApplied) return <NoRecordsFoundMessage />;

      return (
        <CustomNoRecordsIndicator
          indicatorType="orders"
          handleImportFile={() => {
            toggleOpenDialogImportFromFile(true);
          }}
          handleImportInt={() => {
            toggleOpenDialogImportFromIntegration(true);
          }}
        />
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFilterApplied]);

    const updateItem = useCallback(
      (notification) => {
        const infoNotification = JSON.parse(
          notification,
        ) as NotificationRefreshItem;

        if (!ignoreRefreshBySocket) {
          setModifiedItemsIdBySocket(infoNotification.ItemIds);
          toggleExpandedHasChangeOrdersAlert(true);
        }

        setTimeout(() => {
          toggleIgnoreRefreshBySocket(false);
        }, 1000);
      },
      [
        ignoreRefreshBySocket,
        toggleExpandedHasChangeOrdersAlert,
        toggleIgnoreRefreshBySocket,
        setModifiedItemsIdBySocket,
      ],
    );

    useClientOnMethod(hubConnection, METHODS_NAME.REFRESH_ITEM, updateItem);

    return (
      <>
        <Stack>
          {!isUserOnlyReader && (
            <GeoError.Alert
              type="item"
              errorsCount={itemsWithGeoErrors}
              onEdit={() => toogleOpenDialogUpdateGeoError(true)}
              sx={(currentTheme) => ({
                padding: currentTheme.spacing(0, 2, 2, 2),
              })}
            />
          )}

          <DashboardVariantV2GiverContentDataTableContainer>
            <DataTable
              loading={loadingRequestTable}
              data={data}
              columns={columns}
              keyField={ITEM_KEY_ID}
              pagination={{
                options: {
                  sizePerPage: pagination.sizePerPage,
                  page: pagination.page,
                  totalSize,
                },
              }}
              remote={{
                pagination: true,
                sort: true,
              }}
              onTableChange={handleChangeTable}
              selectRow={dataTableSelectRowProps}
              noDataIndicationComponent={getNoRecordsIndicator}
              hideableStorageKey="Dashboard-Giver-In-Preparation-Data-Table"
              toggleHideableColumns
              excludeHideableFieldColumns={['title']}
            />
          </DashboardVariantV2GiverContentDataTableContainer>
        </Stack>

        {openDialogUpdateGeoError && (
          <GeoError.UpdateDialog
            type="item"
            open={openDialogUpdateGeoError}
            onClose={() => toogleOpenDialogUpdateGeoError(false)}
            onSuccessSubmit={reloadDataAndResetSelectedRows}
          />
        )}

        {openDialogDetailsOrder && selectedDataForDetailsOrder && (
          <DialogDetailsOrder
            open={openDialogDetailsOrder}
            onClose={handleCloseDialogDetailsOrder}
            enabledChipTitle={false}
            {...selectedDataForDetailsOrder}
          />
        )}
      </>
    );
  };

export default DashboardVariantV2GiverContentInPreparationTabDataTable;
